import { useEffect } from "react"
import { createHashRouter } from "react-router-dom"
import { DisclaimerPage, EULAPage, PrivacyPolicyPage, TermsOfUsePage } from "@/pages/terms-and-policy/Terms"
import { RoutesPath } from "@/router/config"
import { loginRequest } from "@/config/authConfig"
import { useMsal } from "@azure/msal-react"
import { InteractionStatus } from "@azure/msal-browser"
import { lazyWithRetry } from "@/utils/lazyWithRetry"

// Guards against a second interactive call within the same page load. React
// StrictMode double-invokes effects in dev, and the browser has not navigated
// away yet when the second invocation runs, which would otherwise throw
// `interaction_in_progress`. A full B2C redirect reloads the page and resets
// this flag, so it is safe as module state.
let loginInFlight = false

const AutoLogin = () => {
    const { instance, inProgress } = useMsal()

    useEffect(() => {
        // Wait until MSAL has finished processing any in-flight interaction
        // (e.g. the B2C redirect callback). Triggering loginRedirect() while a
        // redirect is still being handled causes a login loop.
        if (inProgress !== InteractionStatus.None) return
        // If an account already exists we are authenticated; don't redirect.
        if (instance.getAllAccounts().length > 0) return
        // Avoid firing a second interactive request before navigation occurs.
        if (loginInFlight) return

        loginInFlight = true
        instance.loginRedirect(loginRequest).catch(error => {
            loginInFlight = false
            console.error(error)
        })
    }, [instance, inProgress])

    // Minimal full-height placeholder while the redirect is in flight.
    return <div style={{ height: "100vh" }} />
}

const MobileOopsPage = lazyWithRetry(() => import("@/pages/mobileOops"))
const DeploymentPage = lazyWithRetry(() => import("@/pages/deployment"))

const unauthenticatedRouter = createHashRouter([
    {
        path: RoutesPath.TermsOfUse,
        element: <TermsOfUsePage />,
    },
    {
        path: RoutesPath.TermsOfService,
        element: <TermsOfUsePage />,
    },
    {
        path: RoutesPath.PrivacyPolicy,
        element: <PrivacyPolicyPage />,
    },
    {
        path: RoutesPath.Disclaimer,
        element: <DisclaimerPage />,
    },
    {
        path: RoutesPath.EULA,
        element: <EULAPage />,
    },
    {
        path: "/mobile-oops",
        element: <MobileOopsPage />,
    },
    {
        path: RoutesPath.Deployment,
        element: <DeploymentPage />,
    },
    {
        path: "*",
        element: <AutoLogin />,
    },
])

export default unauthenticatedRouter
